import { isEqual } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAsyncEffect from 'use-async-effect/';
import { isMobile } from 'react-device-detect';

import { IAvatarSkin } from '@interfaces/avatarSkin';
import { useMytaverse } from '@providers/MytaverseProvider';
//import { useNotification } from '@components/Notification';
import EventsService from '@services/EventsService';
import ParticipantService from '@services/ParticipantsService';
import { IEventParticipant } from '@interfaces/eventParticipant';
import { IEvent, PlayerSession } from '@interfaces/event';
import { AnalyticsType } from '@interfaces/analytics';
import {
  EventParticipantDataType,
  InitMessageActions,
  ParticipantsStateHookProps,
  SetParticipantStateType,
  UseInitMessageProps,
} from '../interfaces';
import { getInitialMapParticipants } from '@helpers/participant';
import { IParticipant } from '@interfaces/participants';
import { WebsocketAction } from '@interfaces/webSocketConnectionInfo';
import { IInitMessage } from '../../../components/DashboardContent/Pureweb/interfaces';
import { ParticipantPosition } from '../../../components/DashboardContent/interfaces';
import { getSessionStorageValue } from '@helpers/sessionStorage';
import { SessionStorage } from '@constants/storage';
import { MytaverseLogger } from '@helpers/logger';

import { getDeviceProfile } from '../helpers';
import { getAvatarInitials } from '@components/UserAvatar';
import { env } from 'env';
import ParticipantScopes from '@constants/scopes';
import { getClientSpecificInitMessageData } from '@helpers/clientSpecificUtils';
import { isSinglePlayerEvent } from '@helpers/events';

const GAME_SOUND = 0.5;
const PARTICIPANT_SOUND = 1;

export const useEventParticipantData = ({
  currentEvent,
  currentEventParticipant,
}: EventParticipantDataType) => {
  const { selectedLanguage } = useMytaverse();
  const firstRenderSettings = useRef<IEventParticipant | null>(null);
  const settingsRequestTimeout = useRef<NodeJS.Timeout | null>(null);

  const [participantsSound, setParticipantsSound] = useState(PARTICIPANT_SOUND);
  const [gameSound, setGameSound] = useState(GAME_SOUND);

  useAsyncEffect(async () => {
    if (
      firstRenderSettings.current ||
      !currentEvent ||
      !currentEventParticipant
    ) {
      return;
    }

    const {
      participantId,
      selectedLanguage,
      participantsSound = PARTICIPANT_SOUND,
      gameSound = GAME_SOUND,
    } = currentEventParticipant;

    setParticipantsSound(participantsSound);
    setGameSound(gameSound);

    firstRenderSettings.current = {
      eventId: currentEvent.eventId,
      participantId,
      selectedLanguage,
      participantsSound: participantsSound,
      gameSound: gameSound,
    };
  }, [currentEventParticipant, currentEvent]);

  useEffect(() => {
    //TODO create request to set setting
    if (
      !currentEventParticipant ||
      !currentEvent ||
      !firstRenderSettings.current
    ) {
      return;
    }

    const isEqualSettings = isEqual(firstRenderSettings.current, {
      eventId: currentEvent.eventId,
      participantId: currentEventParticipant.participantId,
      selectedLanguage,
      participantsSound,
      gameSound,
    });

    if (isEqualSettings) {
      return;
    }

    const updateEventUserData = async () => {
      if (settingsRequestTimeout.current) {
        clearTimeout(settingsRequestTimeout.current);
        settingsRequestTimeout.current = null;
      }

      settingsRequestTimeout.current = setTimeout(async () => {
        firstRenderSettings.current = {
          eventId: currentEvent.eventId,
          participantId: currentEventParticipant.participantId,
          selectedLanguage,
          participantsSound,
          gameSound,
        };

        await EventsService.setEventUserData(currentEvent.eventId, {
          selectedLanguage,
          participantsSound,
          gameSound,
        });

        settingsRequestTimeout.current = null;
      }, 1000);
    };
    updateEventUserData();

    return () => {
      if (settingsRequestTimeout.current) {
        clearTimeout(settingsRequestTimeout.current);
        settingsRequestTimeout.current = null;
      }
    };
  }, [
    selectedLanguage,
    participantsSound,
    gameSound,
    currentEventParticipant,
    currentEvent,
  ]);

  const setParticipantsSoundHandle = useCallback(
    (level: any) => {
      setParticipantsSound(level);
    },
    [setParticipantsSound],
  );

  const setGameSoundHandle = useCallback(
    (level: number) => {
      setGameSound(level);
    },
    [setGameSound],
  );

  return {
    participantsSound,
    gameSound,
    setGameSound,
    setParticipantsSound,
    setGameSoundHandle,
    setParticipantsSoundHandle,
  };
};

export const useParticipantsState = ({
  currentParticipantId,
  currentEventId,
  currentParticipant,
  setParticipants,
  setCurrentParticipant,
  currentRoom,
  setCurrentRoom,
  currentRegion,
  setCurrentRegion,
  setIsParticipantRoomChanged,
  rooms,
  trackEventAnalytics,
  websocketSessionId,
}: ParticipantsStateHookProps) => {
  const { sendJSONMessageToWebSocket } = useMytaverse();

  const syncEventParticipantsState = useCallback(async () => {
    if (!currentEventId) {
      return;
    }

    const participantsInfo = await EventsService.getEventConnections(
      currentEventId,
    );

    setParticipants((participants) =>
      getInitialMapParticipants(participants, participantsInfo),
    );
    setCurrentParticipant((prev) => {
      if (!prev) {
        return;
      }

      const participantInfo = participantsInfo.find(
        (participantInfo) =>
          participantInfo.participantId === currentParticipantId &&
          participantInfo.sessionId === websocketSessionId &&
          participantInfo.sessionId,
      );

      if (participantInfo && participantInfo.roomId !== prev?.roomId) {
        return {
          ...prev,
          roomId: participantInfo.roomId,
        };
      }

      return prev;
    });
  }, [currentEventId, currentParticipantId]);

  // Set current room
  useAsyncEffect(async () => {
    if (!currentParticipant) {
      return;
    }

    const webSocketMessages = [];

    if (
      currentParticipant.roomId &&
      (!currentRoom ||
        currentRoom.roomId !== currentParticipant.roomId ||
        currentRoom.gameSessionId !== currentParticipant.gameSessionId)
    ) {
      const room = rooms.find(
        ({ roomId }) => roomId === currentParticipant.roomId,
      );

      if (!room) {
        return;
      }

      // Indicate that it's not a newcomer, just room has been changed
      if (currentRoom && currentRoom.roomId) {
        setIsParticipantRoomChanged(true);
      }

      setCurrentRoom({
        ...room,
        gameSessionId: currentParticipant.gameSessionId,
      });

      webSocketMessages.push({
        action: WebsocketAction.JoinedToRoom,
        timestamp: Date.now(),
        participantId: currentParticipant.participantId,
        eventId: currentParticipant.eventId,
        roomId: room.roomId,
        regionName: currentParticipant.region
          ? currentParticipant.region.region
          : null,
        gameSessionId: currentParticipant.gameSessionId,
        isSpeaker: currentParticipant.isSpeaker,
        isDemoUser: currentParticipant.isDemoUser,
      });
    } else if (!currentParticipant.roomId && currentRoom) {
      setCurrentRoom(null);
      setCurrentRegion(null);
    }

    const currentRegionName = currentRegion ? currentRegion.region : null;
    const currentParticipantRegionName = currentParticipant.region
      ? currentParticipant.region.region
      : null;

    if (currentRegionName !== currentParticipantRegionName) {
      setCurrentRegion(currentParticipant.region);

      if (currentParticipant.region) {
        if (currentRegionName) {
          trackEventAnalytics(AnalyticsType.LeftRegion, {
            region: currentRegionName,
            duration:
              currentRegion && currentRegion.timestamp
                ? Date.now() - currentRegion.timestamp
                : 0,
          });
        }

        webSocketMessages.push({
          action: WebsocketAction.JoinedToRegion,
          timestamp: Date.now(),
          participantId: currentParticipant.participantId,
          roomId: currentParticipant.roomId,
          region: currentParticipant.region.region,
        });

        trackEventAnalytics(AnalyticsType.JoinedToRegion, {
          region: currentParticipant.region?.region,
          isPrivate: currentParticipant.region?.isSeatRegion || false,
        });

        MytaverseLogger.log(
          `${currentParticipant.fullName} joined to ${currentParticipant.region?.region} region`,
        );
      }

      if (currentRegionName && !currentParticipantRegionName) {
        webSocketMessages.push({
          action: WebsocketAction.LeftRegion,
          timestamp: Date.now(),
          participantId: currentParticipant.participantId,
          region: currentRegion?.region,
        });

        MytaverseLogger.log(
          `${currentParticipant.fullName} left ${currentRegionName} region`,
        );

        trackEventAnalytics(AnalyticsType.LeftRegion, {
          region: currentRegionName,
          duration:
            currentRegion && currentRegion.timestamp
              ? Date.now() - currentRegion.timestamp
              : 0,
        });
      }
    }

    if (webSocketMessages.length !== 0) {
      await Promise.all(
        webSocketMessages.map((webSocketMessage) =>
          sendJSONMessageToWebSocket(webSocketMessage),
        ),
      );
    }
  }, [currentParticipant, currentRoom, currentRegion, trackEventAnalytics]);

  useEffect(() => {
    const interval = setInterval(async () => {
      await syncEventParticipantsState();
    }, 30000);

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [syncEventParticipantsState]);

  const setParticipantState: SetParticipantStateType = useCallback(
    (participantId, participantState) => {
      if (currentParticipantId === participantId) {
        setCurrentParticipant((prev) => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            ...participantState,
          };
        });
      }

      setParticipants((prev) => {
        const isNewParticipant = !prev.some(
          (participant) => participant.participantId === participantId,
        );

        if (isNewParticipant) {
          return [
            ...prev,
            {
              ...ParticipantService.getEmptyParticipant(participantId),
              ...participantState,
            } as IParticipant,
          ];
        }

        return prev.map((participant) => {
          if (participant.participantId === participantId) {
            return {
              ...participant,
              ...participantState,
            };
          }

          return participant;
        });
      });
    },
    [currentParticipantId],
  );

  return {
    setParticipantState,
  };
};

export const useInitMessage = ({
  token,
  websocketSessionId,
  currentParticipant,
  currentEventParticipant,
  dolbyToken,
  currentEvent,
  teleportingToRoom,
  teleportEnabled,
  user,
  userPermissions,
  generateNewGameliftStreamsSessionKey,
}: UseInitMessageProps) => {
  const storageParticipantPosition =
    getSessionStorageValue<ParticipantPosition>(
      SessionStorage.ParticipantPosition,
    );
  const defaultParticipantPosition = {
    timestamp: 0,
    x: 0,
    y: 0,
    z: 0,
    r: 0,
  };

  const playerSessionEmptyHandle = useCallback(() => {
    // showNotification(
    //   getStreamingNotification({
    //     message: 'playerSession does not exist',
    //   }),
    // );
    // MytaverseLogger.log('playerSession is empty');
    // navigate(ROUTES.SELECT_EVENT);
  }, []);

  const initMessageHandler =
    useCallback(async (): Promise<IInitMessage | null> => {
      if (
        !token ||
        (!isSinglePlayerEvent(currentEvent) && !currentParticipant) ||
        !user ||
        (!isSinglePlayerEvent(currentEvent) && !teleportingToRoom) ||
        !currentEvent
      ) {
        return null;
      }

      const sessionKey = generateNewGameliftStreamsSessionKey();

      const webSocketUrl = new URL(
        env.REACT_APP_SPATIAL_MANAGER_WEB_SOCKET_URL as string,
      );

      webSocketUrl.searchParams.append('token', token as string);
      webSocketUrl.searchParams.append('clientType', 'UE5');
      webSocketUrl.searchParams.append('session', websocketSessionId);

      if (sessionKey) {
        webSocketUrl.searchParams.append('sessionKey', sessionKey);
      }

      let playerSession: PlayerSession | null = null;

      if (!isSinglePlayerEvent(currentEvent) && teleportingToRoom) {
        playerSession = await EventsService.createGameliftPlayerSession(
          currentEvent.eventId,
          teleportingToRoom.roomId,
        );
      }

      if (!playerSession && !isSinglePlayerEvent(currentEvent)) {
        playerSessionEmptyHandle();
        return null;
      }

      // const apiUrl = (env.REACT_APP_API_URL as string).includes(
      //   'localhost',
      // )
      //   ? 'https://api.dev.mytaverse-ek.mytaverse.com'
      //   : (env.REACT_APP_API_URL as string).replace(/\/$/, '');

      const apiUrl = env.REACT_APP_API_URL.replace(/\/$/, '');

      const participantFullName = user.name;
      const [participantFirstName, participantLastName] =
        participantFullName.split(' ');

      const clientExtraData = getClientSpecificInitMessageData({ user });

      let virtualAssistantName: string | null = null;

      if (currentEvent.eventTags) {
        const virtualAssistantTag = currentEvent.eventTags.find((eventTag) =>
          eventTag.startsWith('VirtualAssistant:'),
        );

        if (virtualAssistantTag) {
          const virtualAssistantTagParts = virtualAssistantTag.split(':', 2);

          if (virtualAssistantTagParts.length > 1) {
            virtualAssistantName = virtualAssistantTagParts[1];
          }
        }
      }

      const experienceMode = currentEventParticipant?.experienceMode || '';

      const message: IInitMessage = {
        action: InitMessageActions.INIT,
        token: `Bearer ${token}`,
        webSocketUrl,
        apiUrl,
        participantId: currentParticipant?.participantId || '',
        participantFullName,
        participantFirstName,
        participantLastName: participantLastName || '',
        role: currentEventParticipant?.role || '',
        changePositionInterval: 1,
        eventId: currentEvent.eventId,
        avatarId: user.activeAvatarId || '',
        skinId: null,
        roomId: teleportingToRoom?.roomId || '',
        screenHeight: window.innerHeight >= 1080 ? 1080 : window.innerHeight,
        screenWidth: window.innerWidth >= 1980 ? 1980 : window.innerWidth,
        downloadAvatarUrl: user.customAvatarUrl || '',
        isMobile,
        deviceProfile: getDeviceProfile(),
        dolbyToken: '',
        isSpeaker: currentParticipant?.isSpeaker || false,
        useLastParticipantPosition: !!storageParticipantPosition,
        lastParticipantPosition:
          storageParticipantPosition ?? defaultParticipantPosition,
        gamelift: {
          playerSession,
        },
        mapName: teleportingToRoom?.map?.ueIdentifier || '',
        multiPlayer: !!teleportingToRoom?.isMultiPlayer,
        hrData: user?.clientSpecificData, //TODO: ASK UE TO CHANGE IT
        clientSpecificData: user?.clientSpecificData,
        gender: user?.gender,
        ...(clientExtraData ? { clientExtraData } : {}),
        deactivateTeleport: !teleportEnabled,
        userProfile: getAvatarInitials(
          (user.name || user.email || '').toUpperCase(),
        ),
        experienceMode: experienceMode,
        eventTags: currentEvent?.eventTags ?? [],
        sessionKey,
        ...(virtualAssistantName ? { virtualAssistantName } : {}),
        userPermissions,
        isTrainerTool: userPermissions?.includes(
          ParticipantScopes.HubTrainingToolHubEvent,
        ),
      };

      return message;
    }, [
      token,
      websocketSessionId,
      currentParticipant,
      dolbyToken,
      currentEvent,
      teleportingToRoom,
      teleportEnabled,
      isMobile,
      playerSessionEmptyHandle,
      user,
      userPermissions,
    ]);

  return {
    initMessageHandler,
  };
};

export const useAvatarMessage = ({
  currentSkin,
  currentEvent,
  customAvatarUrl,
}: {
  currentSkin: IAvatarSkin | null;
  currentEvent: IEvent | undefined;
  customAvatarUrl: string | null;
}) => {
  const avatarMessage = useMemo(() => {
    if (!currentEvent) {
      return null;
    }

    const currentAvatarType = currentSkin
      ? currentEvent?.avatars.find(
          (avatar) => avatar.avatarId === currentSkin.avatarId,
        )?.type
      : '';

    return {
      avatarId: currentSkin ? currentSkin.avatarId : '',
      type: currentAvatarType,
      skinId: currentSkin ? currentSkin.avatarSkinId : '',
      downloadAvatarUrl: customAvatarUrl || '',
      avatarType: customAvatarUrl ? 'AVATURN' : '',
    };
  }, [currentSkin, currentEvent, customAvatarUrl]);

  return { avatarMessage };
};
